import React from 'react'
import { INCLUDED_LICENSE_DAYS } from '@adthlete/common/utils/dealPricing'

/** The booking on an offer, as the brand set it up */
export type OfferBooking =
  | {
      kind: 'rate_card'
      theme: { id: string; label: string } | null
      deliverableLabel: string
      deliverable: string
      quantity: number
      unitPrice: number
      licenseDays: number | null
      licensePrice: number | null
    }
  | { kind: 'custom'; description: string; askForRate: boolean; price?: number | null }

const money = (n: number) => `$${n.toLocaleString('en-US')}`

/** One line on what the brand is booking: a rate-card deliverable, or a custom request */
export const BookingSummary: React.FC<{ booking: OfferBooking | null | undefined }> = ({
  booking: b,
}) => {
  if (!b) return null
  if (b.kind === 'custom') {
    return (
      <div className="mt-2 rounded-xl border border-border bg-muted px-3 py-2">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Custom request
        </p>
        <p className="mt-0.5 text-sm text-foreground">
          {b.description}
          {b.price ? ` · ${money(b.price)}` : ''}
        </p>
        {/* A priced item of yours (a signing, a camp) isn't a post: no ad usage */}
        {b.price ? null : (
          <p className="text-xs text-muted-foreground">
            Includes {INCLUDED_LICENSE_DAYS} days for the brand to run it as an ad
          </p>
        )}
      </div>
    )
  }
  const unit = b.deliverable === 'appearance' ? 'hr' : ''
  return (
    <div className="mt-2 rounded-xl border border-border bg-muted px-3 py-2">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        From your rate card
      </p>
      <p className="mt-0.5 text-sm text-foreground">
        {b.quantity} × {b.deliverableLabel}
        {b.theme ? ` · ${b.theme.label}` : ''} at {money(b.unitPrice)}
        {unit ? `/${unit}` : ' each'}
      </p>
      {b.deliverable !== 'appearance' ? (
        <p className="text-xs text-muted-foreground">
          Includes {INCLUDED_LICENSE_DAYS} days for the brand to run it as an ad
          {b.licenseDays
            ? `, extended ${b.licenseDays} more days${b.licensePrice ? ` at ${money(b.licensePrice)} each` : ''}`
            : ''}
        </p>
      ) : null}
    </div>
  )
}

/** Every item in the offer, one line each, with the total when there are several */
export const BookingList: React.FC<{ bookings: OfferBooking[] }> = ({ bookings }) => {
  if (!bookings.length) return null
  if (bookings.length === 1) return <BookingSummary booking={bookings[0]} />
  const total = bookings.reduce(
    (n, b) =>
      n +
      (b.kind === 'rate_card'
        ? (b.unitPrice + (b.licensePrice ?? 0)) * b.quantity
        : (b.price ?? 0)),
    0,
  )
  return (
    <div>
      {bookings.map((b, i) => (
        <BookingSummary key={i} booking={b} />
      ))}
      {total ? (
        <p className="mt-2 text-right text-sm font-semibold text-foreground">
          {bookings.length} items · {money(total)}
        </p>
      ) : null}
    </div>
  )
}

export default BookingSummary
